import { useQuery } from '@tanstack/react-query';
import { fetchClients } from '../../api/features/clientData/clientApi';
import { fetchJobDetails } from '../../api/features/jobDetail/jobDetailApi';
import type { Client } from '../../api/features/clientData/client.types';
import type { SAPInputOption } from '../../components/design-system';

// Client dropdown hook
export const useClientDropdown = () => {
  return useQuery(
    ['clients', 'dropdown'],
    async () => {
      const data = await fetchClients({
        clientCode: 'admin',
        firstResult: -1,
        maxResult: 0,
      });
      return data;
    },
    {
      select: (data: Client[]): SAPInputOption[] => {
        return data.map(client => ({
          label: client.name,
          value: client.id,
          code: client.code,
          disabled: false,
        }));
      },
      staleTime: 10 * 60 * 1000, // Cache for 10 minutes
      cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  );
};

// Job dropdown hook with client dependency
export const useJobDropdown = (clientId?: number | string, enabled: boolean = true) => {
  return useQuery(
    ['jobs', 'dropdown', clientId],
    async () => {
      const params: any = {
        clientCode: 'admin',
        firstResult: -1,
        maxResult: 0,
      };
      
      if (clientId) {
        params.clientId = clientId;
      }
      
      const data = await fetchJobDetails(params);
      return data;
    },
    {
      select: (data: any[]): SAPInputOption[] => {
        return data.map(jobArray => {
          // jobArray format: [jobId, interval, jobName, lastStart, lastEnd, nextRun, status, ...]
          const [jobId, interval, jobName, lastStart, lastEnd, nextRun, status] = jobArray;
          
          return {
            label: jobName || `Job ${jobId}`,
            value: jobId,
            status: status,
            disabled: status === 0, // Disable inactive jobs
          };
        });
      },
      enabled: enabled && !!clientId, // Only fetch when clientId is available and enabled
      staleTime: 5 * 60 * 1000,  // Cache for 5 minutes (jobs change more frequently)
      cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  );
};

// Combined hook for client and job dropdowns
export const useClientJobDropdowns = () => {
  const clientQuery = useClientDropdown();
  
  return {
    // Client dropdown data and methods
    clients: {
      data: clientQuery.data || [],
      loading: clientQuery.isLoading,
      error: clientQuery.error,
      refetch: clientQuery.refetch,
    },
    
    // Factory function to get job dropdown for a specific client
    getJobsForClient: (clientId?: number | string, enabled: boolean = true) => {
      // This would typically be called in a component that manages the selected client
      return useJobDropdown(clientId, enabled);
    },
  };
};

// Activity type dropdown (static options for now, can be made dynamic)
export const useActivityTypeDropdown = () => {
  const activityTypes: SAPInputOption[] = [
    { label: 'Data Load', value: 'DATA_LOAD' },
    { label: 'Integration', value: 'INTEGRATION' },
    { label: 'Transformation', value: 'TRANSFORMATION' },
    { label: 'Validation', value: 'VALIDATION' },
    { label: 'Error Handling', value: 'ERROR_HANDLING' },
    { label: 'System', value: 'SYSTEM' },
  ];

  return {
    data: activityTypes,
    loading: false,
    error: null,
  };
};

// Generic dropdown hook for custom options
export interface UseCustomDropdownOptions<T> {
  queryKey: string[];
  queryFn: () => Promise<T[]>;
  selectFn: (data: T[]) => SAPInputOption[];
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}

export const useCustomDropdown = <T>({
  queryKey,
  queryFn,
  selectFn,
  enabled = true,
  staleTime = 5 * 60 * 1000,
  gcTime = 15 * 60 * 1000,
}: UseCustomDropdownOptions<T>) => {
  return useQuery(
    queryKey,
    queryFn,
    {
      select: selectFn,
      enabled,
      staleTime,
      cacheTime: gcTime,
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }
  );
};

// Error pattern dropdown
export const useErrorPatternDropdown = () => {
  return useQuery(
    ['errorPatterns', 'dropdown'],
    async () => {
      // TODO: Replace with actual API call when available
      const response = await fetch('/api/admin/errorPatternMaster/');
      return response.json();
    },
    {
      select: (data: any[]): SAPInputOption[] => {
        return data.map(pattern => ({
          label: pattern.patternName || pattern.name,
          value: pattern.id,
          code: pattern.patternCode,
          disabled: !pattern.active,
        }));
      },
      enabled: false, // Disable until API is implemented
      staleTime: 15 * 60 * 1000, // Cache for 15 minutes
      cacheTime: 45 * 60 * 1000, // Keep in cache for 45 minutes
    }
  );
};

// Export all hooks for easy import
export const commonDropdownHooks = {
  useClientDropdown,
  useJobDropdown,
  useClientJobDropdowns,
  useActivityTypeDropdown,
  useCustomDropdown,
  useErrorPatternDropdown,
};